import React, { useMemo, useState } from 'react';
import {
  Badge, Box, Button, Chip, CircularProgress, Collapse, Divider, FormControl, InputAdornment, InputLabel, MenuItem, Paper,
  Select, TextField, Tooltip, Typography
} from '@mui/material';
import {
  FileDownload as DownloadIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Tune as TuneIcon
} from '@mui/icons-material';
import { BUCKET_COLORS, BUCKET_LABELS, OPTION_FIELDS, OPTION_LABELS, countAdvancedFilters, hasAnyFilter } from '../constants';
import { fmtCompact, fmtDay, monthLabel } from '../format';
import { BucketKey, JournalFilters, JournalMeta, OptionField, OptionPick } from '../types';
import { OptionFilter, PharmacyFilter, summary } from './FilterControls';

interface Props {
  meta: JournalMeta;
  /** Фильтры в полях ввода (поиск - без задержки) */
  filters: JournalFilters;
  /** Применённые фильтры - для каскадного подбора значений */
  effective: JournalFilters;
  loading: boolean;
  onChange: (patch: Partial<JournalFilters>) => void;
  onPick: (field: OptionField, pick: OptionPick | null) => void;
  onReset: () => void;
  onRefresh: () => void;
  onExport: () => void;
}

const chipSx = { bgcolor: 'rgba(37,99,235,0.07)', color: '#1e40af', fontWeight: 600, borderRadius: 1.5 };
const buttonSx = { height: 40, textTransform: 'none', fontWeight: 600, borderRadius: 1.5 } as const;

const JournalToolbar: React.FC<Props> = ({ meta, filters, effective, loading, onChange, onPick, onReset, onRefresh, onExport }) => {
  const [advancedOpen, setAdvancedOpen] = useState(false);

  const months = useMemo(() => meta.months.slice().sort((a, b) => b.month.localeCompare(a.month)), [meta.months]);
  const pharmacyNames = useMemo(() => {
    const map: Record<string, string> = {};
    meta.pharmacies.forEach(p => { map[p.guid] = p.name; });
    return map;
  }, [meta.pharmacies]);

  const advancedCount = countAdvancedFilters(filters);
  const picks = OPTION_FIELDS.filter(o => filters.picks[o.field]);
  const showChips = filters.pharmacies.length > 0 || advancedCount > 0;

  return (
    <Paper elevation={0} variant="outlined" sx={{ p: 1.5, borderRadius: 2, bgcolor: 'background.paper' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
        <FormControl size="small" sx={{ minWidth: 210 }}>
          <InputLabel shrink>Период записи</InputLabel>
          <Select
            notched displayEmpty label="Период записи" value={filters.month}
            onChange={e => onChange({ month: String(e.target.value) })}
            renderValue={v => monthLabel(String(v) || null)}
            MenuProps={{ PaperProps: { sx: { maxHeight: 420 } } }}
          >
            <MenuItem value="">
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Все периоды</Typography>
            </MenuItem>
            {months.map(m => (
              <MenuItem key={m.month} value={m.month} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                <Typography sx={{ fontSize: '0.85rem' }}>{monthLabel(m.month)}</Typography>
                <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>
                  {m.count.toLocaleString('ru-RU')} зап. · {fmtCompact(m.sum)} ₸ · {m.pharmacies} апт.
                </Typography>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <PharmacyFilter
          options={meta.pharmacies} value={filters.pharmacies} max={meta.limits.pharmacyFilterMax}
          onChange={pharmacies => onChange({ pharmacies })}
        />

        <TextField
          size="small" placeholder="Товар, серия, код, ШК, #ID…" value={filters.search}
          onChange={e => onChange({ search: e.target.value })} sx={{ minWidth: 240, flex: '0 1 300px' }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: 'text.secondary', fontSize: 20 }} /></InputAdornment>
          }}
        />

        <Badge badgeContent={advancedCount} color="primary">
          <Tooltip title={advancedOpen ? 'Скрыть фильтры' : 'Товар, серия, производитель, категория, менеджер, регион, срок годности'}>
            <Button
              variant={advancedOpen ? 'contained' : 'outlined'} color={advancedOpen ? 'primary' : 'inherit'} size="small"
              onClick={() => setAdvancedOpen(o => !o)} aria-label="Дополнительные фильтры"
              sx={{ height: 40, minWidth: 40, width: 40, p: 0, borderRadius: 1.5, borderColor: 'rgba(0, 0, 0, 0.23)' }}
            >
              <TuneIcon fontSize="small" />
            </Button>
          </Tooltip>
        </Badge>

        <Box sx={{ flexGrow: 1 }} />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
          <Button
            variant="outlined" color="inherit" size="small" onClick={onReset} disabled={!hasAnyFilter(filters)}
            sx={{ ...buttonSx, borderColor: '#c4c4c4' }}
          >
            Сброс
          </Button>
          <Button
            variant="contained" size="small" onClick={onRefresh} disabled={loading}
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
            sx={{ ...buttonSx, px: 2 }}
          >
            Обновить
          </Button>
          {meta.access.export && (
            <Tooltip title="Выгрузка записей по текущим фильтрам в Excel / CSV">
              <Button variant="outlined" size="small" color="success" onClick={onExport} startIcon={<DownloadIcon />} sx={buttonSx}>
                Выгрузка
              </Button>
            </Tooltip>
          )}
        </Box>
      </Box>

      {showChips && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1.25, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
          {filters.pharmacies.length > 0 && (
            <Chip
              size="small" label={`Аптеки: ${summary(filters.pharmacies.map(g => pharmacyNames[g] || g))}`}
              onDelete={() => onChange({ pharmacies: [] })} sx={chipSx}
            />
          )}
          {filters.bucket && (
            <Chip
              size="small" label={`Срок: ${BUCKET_LABELS[filters.bucket]}`}
              onDelete={() => onChange({ bucket: '' })} sx={chipSx}
            />
          )}
          {(filters.expiryFrom || filters.expiryTo) && (
            <Chip
              size="small" label={`Годен: ${filters.expiryFrom ? `с ${fmtDay(filters.expiryFrom)}` : ''} ${filters.expiryTo ? `по ${fmtDay(filters.expiryTo)}` : ''}`.trim()}
              onDelete={() => onChange({ expiryFrom: '', expiryTo: '' })} sx={chipSx}
            />
          )}
          {picks.map(o => (
            <Chip
              key={o.field} size="small" label={`${OPTION_LABELS[o.field]}: ${filters.picks[o.field]?.label}`}
              onDelete={() => onPick(o.field, null)} sx={{ ...chipSx, maxWidth: 360 }}
            />
          ))}
        </Box>
      )}

      <Collapse in={advancedOpen} timeout={200} unmountOnExit>
        <Divider sx={{ my: 1.25 }} />
        <Box sx={{ display: 'flex', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1.25 }}>
          {OPTION_FIELDS.map(o => (
            <OptionFilter
              key={o.field} field={o.field} label={o.label} hint={o.hint}
              value={filters.picks[o.field]} filters={effective} onChange={pick => onPick(o.field, pick)}
            />
          ))}

          <FormControl size="small" sx={{ minWidth: 190 }}>
            <InputLabel shrink>Остаток срока</InputLabel>
            <Select
              notched displayEmpty label="Остаток срока" value={filters.bucket}
              onChange={e => onChange({ bucket: e.target.value as BucketKey | '' })}
              renderValue={v => (v ? BUCKET_LABELS[v as BucketKey] : 'Любой')}
            >
              <MenuItem value="">Любой</MenuItem>
              {meta.buckets.map(b => (
                <MenuItem key={b.key} value={b.key}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: BUCKET_COLORS[b.key].color, mr: 1 }} />
                  {b.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            size="small" type="date" label="Годен с" value={filters.expiryFrom}
            onChange={e => onChange({ expiryFrom: e.target.value })} InputLabelProps={{ shrink: true }} sx={{ width: 160 }}
          />
          <TextField
            size="small" type="date" label="Годен по" value={filters.expiryTo}
            onChange={e => onChange({ expiryTo: e.target.value })} InputLabelProps={{ shrink: true }}
            inputProps={{ min: filters.expiryFrom || undefined }} sx={{ width: 160 }}
          />
        </Box>
      </Collapse>
    </Paper>
  );
};

export default React.memo(JournalToolbar);
